import React, { useState, useEffect } from 'react';
import './App.css';
import BachPuzzle from './components/BachPuzzle';
import WelcomeScreen from './components/WelcomeScreen';
import LevelSelector from './components/LevelSelector';
import { 
  GameState, 
  GameScreen, 
  loadGameProgress, 
  saveGameProgress,
  clearGameProgress,
  getStoredProgress
} from './types/GameState';
import { LevelConfig } from './data/levelSystem';
import { getLevelById, allLevels } from './data/levels';

function App() {
  const [gameState, setGameState] = useState<GameState>(() => {
    const progress = loadGameProgress();
    return {
      currentScreen: 'intro',
      currentLevel: null,
      completedLevels: new Set(progress.completedLevels),
      settings: progress.settings
    };
  });

  // Add debug functions to window for easy testing
  useEffect(() => {
    (window as any).clearGameProgress = () => {
      clearGameProgress(); // Clear localStorage
      // Reset React state immediately
      setGameState(prev => ({
        ...prev,
        completedLevels: new Set(),
        settings: { volume: 0.7 }
      }));
      console.log('Game progress cleared and state reset');
    };
    (window as any).getStoredProgress = getStoredProgress;
    (window as any).getCurrentState = () => ({
      completedLevels: Array.from(gameState.completedLevels),
      currentScreen: gameState.currentScreen,
      currentLevel: gameState.currentLevel
    });
    (window as any).markLevelComplete = (levelId: string) => {
      setGameState(prev => ({
        ...prev,
        completedLevels: new Set([...Array.from(prev.completedLevels), levelId])
      }));
      console.log(`Level ${levelId} marked as complete`);
    };
    console.log('Debug functions available: clearGameProgress(), getStoredProgress(), getCurrentState(), markLevelComplete(levelId)');
  }, [gameState.completedLevels, gameState.currentScreen, gameState.currentLevel]);

  // Save progress whenever game state changes
  useEffect(() => {
    const progress = {
      completedLevels: Array.from(gameState.completedLevels),
      settings: gameState.settings
    };
    saveGameProgress(progress);
  }, [gameState.completedLevels, gameState.settings]);

  const goToScreen = (screen: GameScreen) => {
    setGameState(prev => ({
      ...prev,
      currentScreen: screen
    }));
  };

  const selectLevel = (level: LevelConfig) => {
    setGameState(prev => ({
      ...prev,
      currentScreen: 'puzzle',
      currentLevel: level.id
    }));
  };

  const onLevelComplete = (levelId: string) => {
    setGameState(prev => ({
      ...prev,
      completedLevels: new Set([...Array.from(prev.completedLevels), levelId])
    }));
  };

  const renderCurrentScreen = () => {
    switch (gameState.currentScreen) {
      case 'intro':
        return (
          <WelcomeScreen 
            onBegin={() => goToScreen('level-select')}
          />
        );
      
      case 'level-select':
        return (
          <LevelSelector
            completedLevels={gameState.completedLevels}
            onLevelSelect={selectLevel}
            onBack={() => goToScreen('intro')}
          />
        );
      
      case 'puzzle':
        // Get the selected level or default to first level
        const levelId = gameState.currentLevel || 'bach_invention_779';
        const selectedLevel = getLevelById(levelId);
        
        if (!selectedLevel) {
          return <div>Level not found</div>;
        }
        
        // Find next level for victory navigation
        const currentIndex = allLevels.findIndex(l => l.id === levelId);
        const nextLevel = currentIndex >= 0 && currentIndex < allLevels.length - 1 
          ? allLevels[currentIndex + 1] 
          : null;
        
        return (
          <div className="puzzle-screen">
            <BachPuzzle 
              level={selectedLevel}
              onSolved={(solved) => {
                if (solved && gameState.currentLevel) {
                  onLevelComplete(gameState.currentLevel);
                }
              }} 
              onBackToLevels={() => goToScreen('level-select')}
              onNextLevel={nextLevel ? () => selectLevel(nextLevel) : undefined}
            />
          </div>
        );
      
      default:
        return <WelcomeScreen onBegin={() => goToScreen('level-select')} />;
    }
  };

  return (
    <div className="App">
      {renderCurrentScreen()}
    </div>
  );
}

export default App;
